/**
 * @fileoverview Theme hook for dark mode management.
 * @summary Manages theme state and persistence in localStorage.
 *
 * @package KaraoNow
 * @module apps/front/src/hooks/useTheme
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { useState, useEffect } from 'react'

type Theme = 'light' | 'dark'

const THEME_STORAGE_KEY = 'karaonow-theme'

/**
 * @summary Get initial theme from localStorage or system preference.
 * @returns {Theme} Initial theme.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export function getInitialTheme(): Theme {
  // Check localStorage first
  const stored = localStorage.getItem(THEME_STORAGE_KEY) as Theme | null
  if (stored === 'light' || stored === 'dark') {
    return stored
  }

  // Fallback to system preference
  if (typeof window !== 'undefined' && window.matchMedia) {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
    return prefersDark ? 'dark' : 'light'
  }

  // Default to light
  return 'light'
}

/**
 * @summary Apply theme to document.
 * @param {Theme} theme - Theme to apply.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement

  root.setAttribute('data-theme', theme)
  // Classe .dark pour compat / utilitaires dark:
  if (theme === 'dark') {
    root.classList.add('dark')
  } else {
    root.classList.remove('dark')
  }
}

/**
 * @summary Custom hook for theme management.
 * @returns {[Theme, (theme: Theme) => void]} Theme state and setter.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export function useTheme(): [Theme, (theme: Theme) => void] {
  const [theme, setThemeState] = useState<Theme>(getInitialTheme)

  // Apply theme on mount and when it changes
  useEffect(() => {
    applyTheme(theme)
    localStorage.setItem(THEME_STORAGE_KEY, theme)
  }, [theme])

  // Listen to system theme changes
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = (e: MediaQueryListEvent) => {
      // Only update if user hasn't set a preference
      const stored = localStorage.getItem(THEME_STORAGE_KEY)
      if (!stored) {
        setThemeState(e.matches ? 'dark' : 'light')
      }
    }

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }
    // Fallback for older browsers
    mediaQuery.addListener(handleChange)
    return () => mediaQuery.removeListener(handleChange)
  }, [])

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme)
  }

  return [theme, setTheme]
}
